/**
 * Central API client — all UI data access goes through here.
 */

const RAW =
  (import.meta as ImportMeta & { env: { VITE_API_URL?: string } }).env?.VITE_API_URL ||
  "";

export const API_BASE_URL = String(RAW).trim().replace(/\/+$/, "").replace(/\/api$/i, "");

export const AUTH_TOKEN_KEY = "kt_auth_token";

export type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  meta?: Record<string, unknown>;
};

export type ApiResult<T> = {
  data: T;
  meta?: Record<string, unknown>;
};

export function getAuthToken(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setAuthToken(token: string | null) {
  if (typeof localStorage === "undefined") return;
  if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
  else localStorage.removeItem(AUTH_TOKEN_KEY);
}

function authHeader(): HeadersInit {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function apiFetchResult<T>(
  path: string,
  init: RequestInit = {},
): Promise<ApiResult<T>> {
  const endpoint = path.startsWith("/api/")
    ? path
    : path.startsWith("/")
      ? `/api${path}`
      : `/api/${path}`;

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...authHeader(),
      ...(init.headers || {}),
    },
  });

  const payload = (await response.json().catch(() => null)) as ApiResponse<T> | null;
  if (!response.ok || !payload?.success) {
    if (response.status === 401 && typeof localStorage !== "undefined") {
      localStorage.removeItem(AUTH_TOKEN_KEY);
      // Notify AuthProvider in the same tab (storage events do not fire locally).
      window.dispatchEvent(new Event("kt:auth-cleared"));
    }
    const err = new Error(payload?.error || `Request failed (${response.status})`) as Error & {
      status?: number;
      code?: string;
    };
    err.status = response.status;
    err.code = payload?.code;
    throw err;
  }
  return { data: payload.data as T, meta: payload.meta };
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const result = await apiFetchResult<T>(path, init);
  return result.data;
}

export function getHealth() {
  return apiFetch<{
    status: string;
    service: string;
    environment: string;
    timestamp: string;
  }>("/api/health");
}

export function toQuery(params: Record<string, string | number | boolean | undefined | null>) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}
